import { useSelector } from "react-redux";
import { createSelector } from "reselect";

import { RootState } from "store";
import {
  BookSerializersBookByline,
  ListSerializersListBook,
  ListSerializersListBookByline,
  ReadingJournalSerializersJournal,
  TagSerializersBasicTag,
  UserBookSerializersUserBook,
  UserBookSerializersUserBookDate,
} from "types/serializers";

const getState = (state: RootState) => state;

export const geBooksChannel = createSelector(getState, state => state.books);

// Get the books info
export const getBook = (bookId: number) =>
  createSelector(geBooksChannel, ({ books }) => {
    return books[bookId] ? books[bookId].book : null;
  });
export function useBook(bookId: number): BookSerializersBookByline | null {
  return useSelector(getBook(bookId));
}

// Get the currently logged in user's book
export const getUserBook = (bookId: number) =>
  createSelector(geBooksChannel, ({ books }) => {
    return books[bookId] ? books[bookId].userBook : null;
  });
export function useUserBook(
  bookId: number,
): UserBookSerializersUserBook | null {
  return useSelector(getUserBook(bookId));
}

// Get all lists the current user has added the book to
export const getListBooks = (bookId: number) =>
  createSelector(geBooksChannel, ({ books }) => {
    return books[bookId] ? books[bookId].listBooks || [] : [];
  });
export function useListBooks(bookId: number): ListSerializersListBook[] {
  return useSelector(getListBooks(bookId));
}

// Get all tags the current user has added to the book
export const getBookTag = (bookId: number) =>
  createSelector(geBooksChannel, ({ books }) => {
    return books[bookId] ? books[bookId].tags || [] : null;
  });
export function useBookTags(bookId: number): TagSerializersBasicTag[] {
  return useSelector(getBookTag(bookId));
}

// Get all tags the current user has added to the book
export const getJournals = (bookId: number) =>
  createSelector(geBooksChannel, ({ books }) => {
    return books[bookId] ? books[bookId].journals || [] : [];
  });
export function useJournals(
  bookId: number,
): ReadingJournalSerializersJournal[] {
  return useSelector(getJournals(bookId));
}

export const getJournalsCount = (bookId: number) =>
  createSelector(geBooksChannel, ({ books }) => {
    return books[bookId] ? books[bookId].journalsCount || 0 : 0;
  });
export function useJournalsCount(bookId: number): number {
  return useSelector(getJournalsCount(bookId));
}

// Get the UserBookRead for a given bookId / and optional userBookReadId
export const getUserBookRead = (
  bookId: number,
  userBookReadId: number | null,
) =>
  createSelector(geBooksChannel, ({ books }) => {
    const userBook = books[bookId] ? books[bookId].userBook : null;
    if (userBook) {
      if (userBookReadId) {
        return (
          userBook.datesRead.find(ubr => ubr.id === userBookReadId) || null
        );
      } else {
        return userBook.datesRead[userBook.datesRead.length - 1];
      }
    }
    return null;
  });
export function useUserBookRead(
  bookId: number,
  userBookReadId?: number | null,
): UserBookSerializersUserBookDate | null {
  return useSelector(getUserBookRead(bookId, userBookReadId));
}

// Get all dates read for a book
export const getUserBookReads = (bookId: number) =>
  createSelector(geBooksChannel, ({ books }) => {
    const userBook = books[bookId] ? books[bookId].userBook : null;
    return userBook ? userBook.datesRead : [];
  });
export function useUserBookReads(
  bookId: number,
): UserBookSerializersUserBookDate[] {
  return useSelector(getUserBookReads(bookId));
}

// Get List books for a book id and list id
export const getListBook = (bookId: number, listId: number) =>
  createSelector(geBooksChannel, ({ books }) => {
    return books[bookId]?.listBooks?.find(lb => lb?.listId === listId) || null;
  });
export function useListBook(
  bookId: number,
  listId: number,
): ListSerializersListBookByline | null {
  return useSelector(getListBook(bookId, listId));
}
